import { spreadsheet as spreadsheetCore, utils as coreUtils } from '@industriousapps/excelkits-core';
import { isValidNumber } from '@/utils/numbers';
const {
  indexToLetters
} = spreadsheetCore;
// Detects column selections as separate collections
// for each column with valid numeric values.
export function detectNumericColumnSelections(data) {
  let columnsPerGroup = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
  if (!data.length) return [];
  const groups = getColumnGroups(columnsPerGroup, data[0].length);
  const groupSelections = new Map(); // Skip first column (labels) and first row (headers)

  coreUtils.mapMatrix(data.slice(1), (rowIdx, colIdx) => {
    var _groupSelections$get;

    if (colIdx === 0) return; // Skip first column
    // Find which group this column belongs to

    const groupIndex = groups.findIndex(group => colIdx >= group.startIndex && colIdx <= group.endIndex);
    if (groupIndex === -1) return; // Add 1 to rowIdx to account for sliced header

    const value = data[rowIdx + 1][colIdx];
    if (!isValidNumber(value)) return; // Initialize Set for this group if it doesn't exist

    if (!groupSelections.has(groupIndex)) {
      groupSelections.set(groupIndex, new Set());
    }

    (_groupSelections$get = groupSelections.get(groupIndex)) === null || _groupSelections$get === void 0 ? void 0 : _groupSelections$get.add(colIdx);
  });
  const startRow = 2; // 2 represents first data row

  return Array.from(groupSelections.entries()) // Only select groups where all columns have numeric values
  .filter(_ref => {
    let [_, validColumns] = _ref;
    return validColumns.size === columnsPerGroup;
  }).map(_ref2 => {
    let [groupIndex] = _ref2;
    const group = groups[groupIndex];
    const startColumn = indexToLetters(group.startIndex);
    const endColumn = indexToLetters(group.endIndex);
    return `${startColumn}${startRow}:${endColumn}`;
  });
} // Calculate column ranges for each group of columns

export function getColumnGroups(columnsPerGroup, totalColumns) {
  const groups = []; // Start from 1 to skip the labels column

  let currentIndex = 1;

  while (currentIndex < totalColumns) {
    groups.push({
      startIndex: currentIndex,
      endIndex: Math.min(currentIndex + columnsPerGroup - 1, totalColumns - 1)
    });
    currentIndex += columnsPerGroup;
  }

  return groups;
}
// Checks if the workbook data meets the minimum column requirements
export function validateSheetData(activeSheetData, minRequiredColumns) {
  var _activeSheetData$star;

  let skipHeader = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  const startCellIdx = skipHeader ? 0 : 1; // Handle empty data

  if (!activeSheetData.length || !((_activeSheetData$star = activeSheetData[startCellIdx]) !== null && _activeSheetData$star !== void 0 && _activeSheetData$star.length)) {
    return {
      isComplete: false,
      validColumnCount: 0,
      requiredColumnCount: minRequiredColumns
    };
  }

  const columnCount = activeSheetData[startCellIdx].length;
  const columnsWithValidValues = new Set();
  let hasRowWithEnoughValidColumns = false; // Process all rows, starting at row 1 (skip header)

  for (let rowIdx = startCellIdx; rowIdx < activeSheetData.length; rowIdx++) {
    let validColumnsInCurrentRow = 0; // Check each column, starting at column 1 (skip label column)

    for (let colIdx = startCellIdx; colIdx < columnCount; colIdx++) {
      const value = activeSheetData[rowIdx][colIdx];

      if (isValidNumber(value)) {
        columnsWithValidValues.add(colIdx);
        validColumnsInCurrentRow++;
      }
    } // Check if this row has enough valid columns


    if (validColumnsInCurrentRow >= minRequiredColumns) {
      hasRowWithEnoughValidColumns = true;
    }
  }

  const validColumnCount = columnsWithValidValues.size;
  return {
    isComplete: validColumnCount >= minRequiredColumns && hasRowWithEnoughValidColumns,
    validColumnCount,
    requiredColumnCount: minRequiredColumns
  };
}